/**
 * Strings.
 *
 * English and Puerto Rican Spanish are both first-class from the first commit.
 * Retrofitting i18n means finding every string already inlined in a component,
 * and on this account that has meant a translation layer that only ever covered
 * the pages someone remembered.
 *
 * `es-PR`, not generic `es`: the wording is Puerto Rican, and the distinction
 * matters to the people reading it.
 */

export type Locale = 'en' | 'es-PR'

const STRINGS = {
  en: {
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.loading': 'Loading…',

    'auth.handle': 'Username',
    'auth.displayName': 'Your name',
    'auth.createAccount': 'Create account',
    'auth.usePasskey': 'Sign in with a passkey',
    'auth.noPasskeySupport': 'This browser cannot use passkeys. Try Chrome, Safari or Edge.',
    'auth.setupToken': 'Setup token',
    'auth.cancelled': 'That was cancelled. Nothing has changed.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Add a passkey',
    'account.removePasskey': 'Remove',
    'account.synced': 'Synced to your password manager',
    'account.deviceOnly': 'This device only',
    'account.neverUsed': 'Never used',
    'account.lastUsed': 'Last used',
    'account.recoveryWarning':
      'This account has one passkey and it is not synced. Lose that device and the account cannot be recovered — add a second passkey.',

    'notify.enable': 'Turn on notifications',
    'notify.enabled': 'Notifications are on',
    'notify.blocked': 'Notifications are blocked in your browser settings.',
    'notify.inbox': 'Notifications',
    'notify.empty': 'Nothing yet.',

    'admin.title': 'Admin',
    'admin.users': 'Users',
    'admin.audit': 'Activity',
    'admin.errors': 'Errors',
    'admin.flags': 'Flags',
    'admin.settings': 'Settings',
    'admin.broadcast': 'Broadcast',
    'admin.devices': 'Devices',

    'env.beta': 'BETA — this is not the live app',
    'env.local': 'LOCAL',

    'error.generic': 'Something went wrong.',
    'error.offline': 'You appear to be offline.',

    'update.ready': 'A newer version is ready',
    'update.now': 'Update now',
    'update.later': 'Later',

    'install.title': 'Put this on your home screen',
    'install.why': 'It gets its own icon and opens without the browser bar.',
    'install.ios': 'Tap Share, then “Add to Home Screen”.',
    'install.add': 'Add it',
    'install.no': 'No thanks',

    /* The bug door. Plain words on purpose: a child on the tablet is the person
       most likely to press this, and "submit an issue" is not what they call it. */
    'bug.report': 'Report a bug',
    'bug.what': 'What went wrong?',
    'bug.detail': 'Anything else? What were you doing?',
    'bug.attached': 'The version, the page and your device are sent with it.',
    'bug.send': 'Send it',
    'bug.thanks': 'Got it. That is now a ticket.',
    'bug.another': 'Report another',
    'bug.close': 'Close',

    'admin.migrations': 'Migrations',
    'admin.tickets': 'Tickets',

    /* Smash Down itself. The words are the ones used in the room: cars race,
       they do not "compete", and a bracket is a bracket. */
    'garage.title': 'The garage',
    'garage.empty': 'No cars yet. Add the first one below.',
    'garage.add': 'Add a car',
    'garage.carName': 'Which car?',
    'garage.photo': 'Photo (optional)',
    'garage.photoFailed': 'That photo would not upload. The car was not added.',
    'garage.retire': 'Retire',
    'garage.unretire': 'Back in',
    'garage.retired': 'retired',
    'garage.showRetired': 'Show retired cars',
    'garage.hideRetired': 'Hide retired cars',
    'garage.scoreHint': 'Win rate, weighted by how much racing backs it up',

    'race.title': 'Record a race',
    'race.whoWon': 'Who won?',
    'race.whoLost': 'Who did it beat?',
    'race.change': 'Change',
    'race.recorded': 'Recorded.',
    'race.recent': 'Recent races',
    'race.beat': 'beat',
    'race.undo': 'Void',
    'race.voided': 'voided',
    'race.needTwo': 'Add a second car before recording a race.',

    'bracket.title': 'Brackets',
    'bracket.new': 'New bracket',
    'bracket.size': 'How many cars?',
    'bracket.create': 'Draw it',
    'bracket.open': 'Open',
    'bracket.complete': 'Finished',
    'bracket.abandoned': 'Abandoned',
    'bracket.winner': 'Winner',
    'bracket.waiting': 'Waiting',
    'bracket.tapWinner': 'Tap the car that won',
    'bracket.abandon': 'Abandon',
    'bracket.back': 'All brackets',
    'bracket.none': 'No brackets yet.',
  },

  'es-PR': {
    'app.signIn': 'Entrar',
    'app.signOut': 'Salir',
    'app.loading': 'Cargando…',

    'auth.handle': 'Usuario',
    'auth.displayName': 'Tu nombre',
    'auth.createAccount': 'Crear cuenta',
    'auth.usePasskey': 'Entrar con passkey',
    'auth.noPasskeySupport': 'Este navegador no puede usar passkeys. Prueba Chrome, Safari o Edge.',
    'auth.setupToken': 'Código de configuración',
    'auth.cancelled': 'Se canceló. No cambió nada.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Añadir un passkey',
    'account.removePasskey': 'Quitar',
    'account.synced': 'Sincronizado con tu gestor de contraseñas',
    'account.deviceOnly': 'Solo en este equipo',
    'account.neverUsed': 'Nunca se ha usado',
    'account.lastUsed': 'Último uso',
    'account.recoveryWarning':
      'Esta cuenta tiene un solo passkey y no está sincronizado. Si pierdes ese equipo, no se puede recuperar la cuenta — añade un segundo passkey.',

    'notify.enable': 'Activar notificaciones',
    'notify.enabled': 'Las notificaciones están activadas',
    'notify.blocked': 'Las notificaciones están bloqueadas en tu navegador.',
    'notify.inbox': 'Notificaciones',
    'notify.empty': 'Nada todavía.',

    'admin.title': 'Administración',
    'admin.users': 'Usuarios',
    'admin.audit': 'Actividad',
    'admin.errors': 'Errores',
    'admin.flags': 'Flags',
    'admin.settings': 'Ajustes',
    'admin.broadcast': 'Mensaje',
    'admin.devices': 'Equipos',

    'env.beta': 'BETA — esta no es la app en vivo',
    'env.local': 'LOCAL',

    'error.generic': 'Algo salió mal.',
    'error.offline': 'Parece que no tienes conexión.',

    'update.ready': 'Hay una versión más nueva',
    'update.now': 'Actualizar',
    'update.later': 'Ahora no',

    'install.title': 'Ponlo en tu pantalla de inicio',
    'install.why': 'Tiene su propio ícono y abre sin la barra del navegador.',
    'install.ios': 'Toca Compartir y luego “Añadir a inicio”.',
    'install.add': 'Añadir',
    'install.no': 'Ahora no',

    'bug.report': 'Reportar un problema',
    'bug.what': '¿Qué pasó?',
    'bug.detail': '¿Algo más? ¿Qué estabas haciendo?',
    'bug.attached': 'Se envía con la versión, la página y tu equipo.',
    'bug.send': 'Enviar',
    'bug.thanks': 'Recibido. Ya es un ticket.',
    'bug.another': 'Reportar otro',
    'bug.close': 'Cerrar',

    'admin.migrations': 'Migraciones',
    'admin.tickets': 'Tickets',

    'garage.title': 'El garaje',
    'garage.empty': 'Todavía no hay carros. Añade el primero abajo.',
    'garage.add': 'Añadir un carro',
    'garage.carName': '¿Cuál carro?',
    'garage.photo': 'Foto (opcional)',
    'garage.photoFailed': 'La foto no subió. El carro no se añadió.',
    'garage.retire': 'Retirar',
    'garage.unretire': 'Devolver',
    'garage.retired': 'retirado',
    'garage.showRetired': 'Ver los retirados',
    'garage.hideRetired': 'Ocultar los retirados',
    'garage.scoreHint': 'Porcentaje de victorias, pesado por cuántas carreras lo respaldan',

    'race.title': 'Anotar una carrera',
    'race.whoWon': '¿Quién ganó?',
    'race.whoLost': '¿A quién le ganó?',
    'race.change': 'Cambiar',
    'race.recorded': 'Anotado.',
    'race.recent': 'Carreras recientes',
    'race.beat': 'le ganó a',
    'race.undo': 'Anular',
    'race.voided': 'anulada',
    'race.needTwo': 'Añade un segundo carro antes de anotar una carrera.',

    'bracket.title': 'Brackets',
    'bracket.new': 'Bracket nuevo',
    'bracket.size': '¿Cuántos carros?',
    'bracket.create': 'Dibujarlo',
    'bracket.open': 'Abierto',
    'bracket.complete': 'Terminado',
    'bracket.abandoned': 'Abandonado',
    'bracket.winner': 'Ganador',
    'bracket.waiting': 'Esperando',
    'bracket.tapWinner': 'Toca el carro que ganó',
    'bracket.abandon': 'Abandonar',
    'bracket.back': 'Todos los brackets',
    'bracket.none': 'Todavía no hay brackets.',
  },
} as const

export type StringKey = keyof (typeof STRINGS)['en']

let locale: Locale = detect()

function detect(): Locale {
  const stored = localStorage.getItem('locale')
  if (stored === 'en' || stored === 'es-PR') return stored
  return navigator.language.toLowerCase().startsWith('es') ? 'es-PR' : 'en'
}

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  locale = next
  localStorage.setItem('locale', next)
  document.documentElement.lang = next
}

/**
 * A missing translation falls back to English rather than rendering the key.
 * A user seeing `account.passkeys` learns nothing; a user seeing the English
 * learns what the control does.
 */
export function t(key: StringKey): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key
}
